import React, { useState, useEffect, useRef } from 'react'
import { Toast } from '../../../components/ui/Toast.tsx'
import { Button } from '../../../components/ui/Button'
import { BindingAdminService, type BindingDetail, type ProcedureBindingAdmin, type LintResult, type StateRouteBinding } from '../../../services/bindingAdminService'
import { ProcedureTypeService, type ProcedureType } from '../../../services/procedureTypeService'
import { FormVersionService, type FormVersion } from '../../../services/formVersionService'
import { WorkflowService, type WorkflowModelVersion, type StateDefinition, type WorkflowTransition } from '../../../services/workflowService'
import { ApprovalRouteVersionService, type ApprovalRouteVersion } from '../../../services/approvalRouteVersionService'
import { getCurrentUserId } from '../../../services/api'

interface BindingDetailPageProps {
  onNavigate: (page: string, data?: any) => void
  bindingId?: string
}

type TabType = 'state-routes' | 'lint'

export function BindingDetailPage({ onNavigate, bindingId }: BindingDetailPageProps) {
  const isNewBinding = !bindingId

  // 基本データ
  const [binding, setBinding] = useState<ProcedureBindingAdmin | null>(null)
  const [detail, setDetail] = useState<BindingDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // フォームデータ
  const [procedureTypeCode, setProcedureTypeCode] = useState('')
  const [formVersionId, setFormVersionId] = useState('')
  const [workflowVersionId, setWorkflowVersionId] = useState('')
  const [approvalRouteVersionId, setApprovalRouteVersionId] = useState('')
  const [description, setDescription] = useState('')

  // 選択肢データ
  const [procedureTypes, setProcedureTypes] = useState<ProcedureType[]>([])
  const [formVersions, setFormVersions] = useState<FormVersion[]>([])
  const [workflowVersions, setWorkflowVersions] = useState<WorkflowModelVersion[]>([])
  const [approvalRouteVersions, setApprovalRouteVersions] = useState<ApprovalRouteVersion[]>([])

  // 検索クエリ
  const [formSearchQuery, setFormSearchQuery] = useState('')
  const [workflowSearchQuery, setWorkflowSearchQuery] = useState('')
  const [approvalRouteSearchQuery, setApprovalRouteSearchQuery] = useState('')

  // タブ
  const [activeTab, setActiveTab] = useState<TabType | null>('state-routes')

  // Lint結果
  const [lintResult, setLintResult] = useState<LintResult | null>(null)
  const [lintLoading, setLintLoading] = useState(false)

  // State-Route Bindings
  const [stateRouteBindings, setStateRouteBindings] = useState<StateRouteBinding[]>([])
  const [workflowStates, setWorkflowStates] = useState<StateDefinition[]>([])
  const [workflowTransitions, setWorkflowTransitions] = useState<WorkflowTransition[]>([])
  const [approvalRouteKeys, setApprovalRouteKeys] = useState<string[]>([])
  const [approvalRoutesDetail, setApprovalRoutesDetail] = useState<{ [routeKey: string]: any }>({})
  const [stateRouteLoading, setStateRouteLoading] = useState(false)

  // Toast
  const [toastMessage, setToastMessage] = useState('')
  const [showToast, setShowToast] = useState(false)

  // Refs
  const procedureTypeCodeSelectRef = useRef<HTMLSelectElement>(null)
  const formVersionSelectRef = useRef<HTMLSelectElement>(null)
  const workflowVersionSelectRef = useRef<HTMLSelectElement>(null)
  const approvalRouteVersionSelectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    loadInitialData()
  }, [bindingId])

  useEffect(() => {
    loadFormVersions()
  }, [formSearchQuery])

  useEffect(() => {
    loadWorkflowVersions()
  }, [workflowSearchQuery])

  useEffect(() => {
    loadApprovalRouteVersions()
  }, [approvalRouteSearchQuery])

  useEffect(() => {
    if (!isNewBinding && workflowVersionId && approvalRouteVersionId) {
      loadStateRouteData()
    }
  }, [workflowVersionId, approvalRouteVersionId, isNewBinding])

  const loadInitialData = async () => {
    try {
      setLoading(true)
      setError(null)

      // 申請種別一覧取得
      const ptResult = await ProcedureTypeService.getAll({ active: true })
      setProcedureTypes(ptResult.items)

      if (isNewBinding) {
        setLoading(false)
      } else {
        // 既存バインド取得
        const result = await BindingAdminService.getBinding(bindingId!)
        setDetail(result)
        setBinding(result.binding)

        // フォームに反映
        setProcedureTypeCode(result.binding.procedureTypeCode)
        setFormVersionId(result.binding.formVersionId)
        setWorkflowVersionId(result.binding.workflowVersionId)
        setApprovalRouteVersionId(result.binding.approvalRouteVersionId)
        setDescription(result.binding.description || '')

        setLoading(false)
      }
    } catch (err) {
      console.error('Failed to load binding:', err)
      setError('バインド詳細の取得に失敗しました')
      setLoading(false)
    }
  }

  const loadFormVersions = async () => {
    try {
      const result = await FormVersionService.getAll({
        q: formSearchQuery || undefined,
        status: 'PUBLISHED'
      })
      setFormVersions(result)
    } catch (err) {
      console.error('Failed to load form versions:', err)
    }
  }

  const loadWorkflowVersions = async () => {
    try {
      const result = await WorkflowService.getAll({
        q: workflowSearchQuery || undefined,
        status: 'PUBLISHED'
      })
      setWorkflowVersions(result.items)
    } catch (err) {
      console.error('Failed to load workflow versions:', err)
    }
  }

  const loadApprovalRouteVersions = async () => {
    try {
      const result = await ApprovalRouteVersionService.getAll({
        q: approvalRouteSearchQuery || undefined,
        status: 'PUBLISHED'
      })
      setApprovalRouteVersions(result)
    } catch (err) {
      console.error('Failed to load approval route versions:', err)
    }
  }

  const loadStateRouteData = async () => {
    if (!bindingId) return

    try {
      setStateRouteLoading(true)

      const bindings = await BindingAdminService.getStateRoutes(bindingId)
      setStateRouteBindings(bindings)

      if (workflowVersionId) {
        const workflow = await WorkflowService.getById(workflowVersionId)
        setWorkflowStates(workflow.states || [])
        setWorkflowTransitions(workflow.transitions || [])
      }

      if (approvalRouteVersionId) {
        const approvalRoute = await ApprovalRouteVersionService.getById(approvalRouteVersionId)
        const routes = typeof approvalRoute.routes === 'string'
          ? JSON.parse(approvalRoute.routes)
          : approvalRoute.routes
        setApprovalRouteKeys(Object.keys(routes || {}))
        setApprovalRoutesDetail(routes || {})
      }
    } catch (err) {
      console.error('Failed to load state-route data:', err)
      setError('ステータス/承認ルート割当データの取得に失敗しました')
    } finally {
      setStateRouteLoading(false)
    }
  }

  const handleSaveDraft = async () => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    // 申請種別コードのバリデーション
    if (!procedureTypeCode) {
      if (procedureTypeCodeSelectRef.current) {
        procedureTypeCodeSelectRef.current.setCustomValidity('申請種別コードを選択してください')
        procedureTypeCodeSelectRef.current.reportValidity()
      }
      return
    }

    // フォームバージョンのバリデーション
    if (!formVersionId) {
      if (formVersionSelectRef.current) {
        formVersionSelectRef.current.setCustomValidity('フォームバージョンを選択してください')
        formVersionSelectRef.current.reportValidity()
      }
      return
    }

    // ワークフローバージョンのバリデーション
    if (!workflowVersionId) {
      if (workflowVersionSelectRef.current) {
        workflowVersionSelectRef.current.setCustomValidity('ワークフローバージョンを選択してください')
        workflowVersionSelectRef.current.reportValidity()
      }
      return
    }

    // 承認ルートバージョンのバリデーション
    if (!approvalRouteVersionId) {
      if (approvalRouteVersionSelectRef.current) {
        approvalRouteVersionSelectRef.current.setCustomValidity('承認ルートバージョンを選択してください')
        approvalRouteVersionSelectRef.current.reportValidity()
      }
      return
    }

    try {
      setError(null)

      if (isNewBinding) {
        const result = await BindingAdminService.createBinding({
          procedureTypeCode,
          formVersionId,
          workflowVersionId,
          approvalRouteVersionId,
          description: description || undefined,
          actorUserId: currentUserId
        })
        setToastMessage('バインドを作成しました')
        setShowToast(true)
        onNavigate('binding-detail', { bindingId: result.bindingId })
      } else {
        await BindingAdminService.updateBinding(bindingId!, {
          formVersionId,
          workflowVersionId,
          approvalRouteVersionId,
          description: description || undefined,
          actorUserId: currentUserId,
          version: binding!.version
        })
        setToastMessage('バインドを更新しました')
        setShowToast(true)
        await loadInitialData()
      }
    } catch (err: any) {
      console.error('Save failed:', err)
      setError(err.response?.data?.message || '保存に失敗しました')
    }
  }

  const handleLint = async () => {
    if (isNewBinding) {
      setError('バインドを保存してから整合性チェックを実行してください')
      return
    }

    try {
      setLintLoading(true)
      setError(null)
      const result = await BindingAdminService.lintBinding(bindingId!)
      setLintResult(result)
      setActiveTab('lint')
    } catch (err) {
      console.error('Lint failed:', err)
      setError('整合性チェックに失敗しました')
    } finally {
      setLintLoading(false)
    }
  }

  const handleActivate = async () => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを有効化しますか？\n同じ申請種別の既存有効バインドは自動的に無効化されます。')) return

    try {
      setError(null)
      await BindingAdminService.activateBinding(bindingId!, currentUserId)
      setToastMessage('バインドを有効化しました')
      setShowToast(true)
      await loadInitialData()
    } catch (err: any) {
      console.error('Activate failed:', err)
      setError(err.response?.data?.message || '有効化に失敗しました')
    }
  }

  const handleDeactivate = async () => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを無効化しますか？')) return

    try {
      setError(null)
      await BindingAdminService.deactivateBinding(bindingId!, currentUserId)
      setToastMessage('バインドを無効化しました')
      setShowToast(true)
      await loadInitialData()
    } catch (err: any) {
      console.error('Deactivate failed:', err)
      setError(err.response?.data?.message || '無効化に失敗しました')
    }
  }

  const handleArchive = async () => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを廃止しますか？\n廃止後は元に戻せません。')) return

    try {
      setError(null)
      await BindingAdminService.archiveBinding(bindingId!, currentUserId)
      setToastMessage('バインドを廃止しました')
      setShowToast(true)
      await loadInitialData()
    } catch (err: any) {
      console.error('Archive failed:', err)
      setError(err.response?.data?.message || '廃止に失敗しました')
    }
  }

  const handleDuplicate = async () => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!confirm('このバインドを複製しますか？')) return

    try {
      setError(null)
      const result = await BindingAdminService.duplicateBinding(bindingId!, currentUserId)
      setToastMessage('バインドを複製しました')
      setShowToast(true)
      onNavigate('binding-detail', { bindingId: result.bindingId })
    } catch (err: any) {
      console.error('Duplicate failed:', err)
      setError(err.response?.data?.message || '複製に失敗しました')
    }
  }

  const handleSaveStateRoutes = async () => {
    const currentUserId = getCurrentUserId()
    if (!currentUserId) {
      setError('ユーザーIDが取得できません')
      return
    }

    if (!bindingId) {
      setError('バインドを保存してからステータス/承認ルート割当を設定してください')
      return
    }

    try {
      setError(null)
      await BindingAdminService.updateStateRoutes(bindingId, stateRouteBindings, currentUserId)
      setToastMessage('ステータス/承認ルート割当を保存しました')
      setShowToast(true)
      await loadStateRouteData()
    } catch (err: any) {
      console.error('Save state routes failed:', err)
      setError(err.response?.data?.message || 'ステータス/承認ルート割当の保存に失敗しました')
    }
  }

  const handleStateRouteChange = (stateKey: string, routeKey: string) => {
    const existing = stateRouteBindings.find(b => b.stateKey === stateKey)

    if (existing) {
      setStateRouteBindings(
        stateRouteBindings.map(b =>
          b.stateKey === stateKey ? { ...b, routeKey } : b
        )
      )
    } else {
      setStateRouteBindings([
        ...stateRouteBindings,
        {
          bindingId: '',
          workflowVersionId,
          approvalRouteVersionId,
          stateKey,
          routeKey,
          procedureTypeCode: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: '',
          updatedBy: '',
          version: 1
        }
      ])
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; label: string }> = {
      ACTIVE: { bg: '#dcfce7', text: '#166534', label: '有効' },
      DRAFT: { bg: '#f3f4f6', text: '#374151', label: '下書き' },
      INACTIVE: { bg: '#fef3c7', text: '#92400e', label: '無効' },
      ARCHIVED: { bg: '#fee2e2', text: '#991b1b', label: '廃止' }
    }
    const config = styles[status] || styles.DRAFT
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        fontSize: '0.75rem',
        fontWeight: 600,
        borderRadius: '0.375rem',
        backgroundColor: config.bg,
        color: config.text
      }}>
        {config.label}
      </span>
    )
  }

  const isEditable = isNewBinding || (binding && (binding.status === 'DRAFT' || binding.status === 'INACTIVE'))
  const canActivate = binding && binding.status === 'DRAFT'
  const canDeactivate = binding && binding.status === 'ACTIVE'
  const canArchive = binding && binding.status === 'INACTIVE'

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">読み込み中...</div>
      </div>
    )
  }

  return (
    <div className="px-8 py-6">
      <Toast
        message={toastMessage}
        show={showToast}
        onClose={() => setShowToast(false)}
      />

      <div className="page-header">
        <button
          onClick={() => onNavigate('bindings-list')}
          className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          style={{ marginBottom: '1.5rem' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          一覧に戻る
        </button>
        <div style={{
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '0.75rem',
          marginBottom: '2rem'
        }}>
          <h1 className="page-title">
            {isNewBinding ? '新規申請バインド作成' : '申請バインド詳細'}
          </h1>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #e5e7eb',
          color: '#b91c1c',
          padding: '1rem',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* アクション */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">アクション</h2>
        <div className="flex gap-4">
          {isEditable && (
            <button
              onClick={handleSaveDraft}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                border: '1px solid #2563eb',
                borderRadius: '0.375rem',
                color: 'white',
                backgroundColor: '#2563eb',
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1d4ed8'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb'
              }}
            >
              保存
            </button>
          )}

          {!isNewBinding && (
            <>
              <Button onClick={handleLint} variant="secondary" disabled={lintLoading}>
                {lintLoading ? '実行中...' : '整合性チェック'}
              </Button>

              {canActivate && (
                <Button onClick={handleActivate} variant="primary">
                  有効化
                </Button>
              )}

              {canDeactivate && (
                <Button onClick={handleDeactivate} variant="secondary">
                  無効化
                </Button>
              )}

              {canArchive && (
                <Button onClick={handleArchive} variant="secondary">
                  廃止
                </Button>
              )}

              <Button onClick={handleDuplicate} variant="secondary">
                複製して編集
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 基本情報 */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <h2 className="text-lg font-semibold text-gray-900">基本情報</h2>
            {binding && getStatusBadge(binding.status)}
          </div>
        </div>
        <div className="space-y-6">
          {/* 申請種別コード */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              申請種別コード <span className="text-red-600">*</span>
            </label>
            <select
              ref={procedureTypeCodeSelectRef}
              value={procedureTypeCode}
              onChange={(e) => {
                setProcedureTypeCode(e.target.value)
                if (procedureTypeCodeSelectRef.current) {
                  procedureTypeCodeSelectRef.current.setCustomValidity('')
                }
              }}
              disabled={!isNewBinding}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">選択してください</option>
              {procedureTypes.map((pt) => (
                <option key={pt.procedureTypeCode} value={pt.procedureTypeCode}>
                  {pt.procedureTypeCode} - {pt.procedureName}
                </option>
              ))}
            </select>
            {!isNewBinding && (
              <p className="text-xs text-gray-500 mt-1">※ 申請種別コードは変更できません</p>
            )}
          </div>

          {/* フォームバージョン */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              フォームバージョン <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={formSearchQuery}
              onChange={(e) => setFormSearchQuery(e.target.value)}
              placeholder="フォーム名で検索..."
              disabled={!isEditable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 mb-2"
            />
            <select
              ref={formVersionSelectRef}
              value={formVersionId}
              onChange={(e) => {
                setFormVersionId(e.target.value)
                if (formVersionSelectRef.current) {
                  formVersionSelectRef.current.setCustomValidity('')
                }
              }}
              disabled={!isEditable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">選択してください</option>
              {formVersions.map((fv) => (
                <option key={fv.formVersionId} value={fv.formVersionId}>
                  {fv.formName} ({fv.formVersionId})
                </option>
              ))}
            </select>
          </div>

          {/* ワークフローバージョン */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ワークフローバージョン <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={workflowSearchQuery}
              onChange={(e) => setWorkflowSearchQuery(e.target.value)}
              placeholder="ワークフロー名で検索..."
              disabled={!isEditable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 mb-2"
            />
            <select
              ref={workflowVersionSelectRef}
              value={workflowVersionId}
              onChange={(e) => {
                setWorkflowVersionId(e.target.value)
                if (workflowVersionSelectRef.current) {
                  workflowVersionSelectRef.current.setCustomValidity('')
                }
              }}
              disabled={!isEditable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">選択してください</option>
              {workflowVersions.map((wv) => (
                <option key={wv.workflowVersionId} value={wv.workflowVersionId}>
                  {wv.workflowName} ({wv.workflowVersionId})
                </option>
              ))}
            </select>
          </div>

          {/* 承認ルートバージョン */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              承認ルートバージョン <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              value={approvalRouteSearchQuery}
              onChange={(e) => setApprovalRouteSearchQuery(e.target.value)}
              placeholder="ルート名で検索..."
              disabled={!isEditable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 mb-2"
            />
            <select
              ref={approvalRouteVersionSelectRef}
              value={approvalRouteVersionId}
              onChange={(e) => {
                setApprovalRouteVersionId(e.target.value)
                if (approvalRouteVersionSelectRef.current) {
                  approvalRouteVersionSelectRef.current.setCustomValidity('')
                }
              }}
              disabled={!isEditable}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="">選択してください</option>
              {approvalRouteVersions.map((arv) => (
                <option key={arv.approvalRouteVersionId} value={arv.approvalRouteVersionId}>
                  {arv.routeName} ({arv.approvalRouteVersionId})
                </option>
              ))}
            </select>
          </div>

          {/* 説明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">説明</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!isEditable}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="バインドの説明を入力..."
            />
          </div>
        </div>
      </div>

      {/* タブ */}
      {!isNewBinding && (
        <>
          <div style={{
            borderBottom: '2px solid #e5e7eb',
            marginBottom: '1.5rem',
            display: 'flex',
            gap: '2rem'
          }}>
            <button
              onClick={() => setActiveTab(activeTab === 'state-routes' ? null : 'state-routes')}
              style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: activeTab === 'state-routes' ? '#2563eb' : '#6b7280',
                borderBottom: activeTab === 'state-routes' ? '3px solid #2563eb' : '3px solid transparent',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              ステータス/承認ルート割当
            </button>
            <button
              onClick={() => setActiveTab(activeTab === 'lint' ? null : 'lint')}
              style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: activeTab === 'lint' ? '#2563eb' : '#6b7280',
                borderBottom: activeTab === 'lint' ? '3px solid #2563eb' : '3px solid transparent',
                background: 'none',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              整合性チェック
            </button>
          </div>

          {activeTab === 'state-routes' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">ステータス/承認ルート割当</h2>

              {stateRouteLoading ? (
                <div className="text-center py-8 text-gray-500">読み込み中...</div>
              ) : !workflowVersionId || !approvalRouteVersionId ? (
                <div className="text-center py-8 text-gray-500">
                  ワークフローと承認ルートを選択してください
                </div>
              ) : (
                <>
                  <div style={{
                    backgroundColor: '#f9fafb',
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    marginBottom: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#6b7280'
                  }}>
                    ワークフローの各状態（遷移先）に対して、その状態に遷移する際に使用する承認ルートキーを割り当てます。
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8fafc' }}>
                      <tr>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>
                          遷移元
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>
                          遷移先
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>
                          アクション
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', border: '1px solid #e5e7eb' }}>
                          承認ルート
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {workflowTransitions.map((transition, index) => {
                        const fromState = workflowStates.find(s => s.key === transition.from)
                        const toState = workflowStates.find(s => s.key === transition.to)
                        const binding = stateRouteBindings.find(b => b.stateKey === transition.to)
                        // approveまたはsubmitの場合に承認ルート選択を表示
                        const isApprove = transition.action === 'approve' || transition.action === 'submit'
                        const selectedRouteKey = binding?.routeKey || ''

                        return (
                          <tr key={`${transition.from}-${transition.to}-${index}`}>
                            <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                              {fromState && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div
                                    style={{
                                      width: '0.75rem',
                                      height: '0.75rem',
                                      borderRadius: '50%',
                                      backgroundColor: fromState.bgColor
                                    }}
                                  />
                                  <span style={{ fontWeight: '500' }}>{fromState.label}</span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                              {toState && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <div
                                    style={{
                                      width: '0.75rem',
                                      height: '0.75rem',
                                      borderRadius: '50%',
                                      backgroundColor: toState.bgColor
                                    }}
                                  />
                                  <span style={{ fontWeight: '500' }}>{toState.label}</span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb' }}>
                              {transition.action || 'ー'}
                            </td>
                            <td style={{ padding: '0.75rem', border: '1px solid #e5e7eb', backgroundColor: !isApprove ? '#f3f4f6' : 'transparent' }}>
                              {isApprove ? (
                                <select
                                  value={selectedRouteKey}
                                  onChange={(e) => handleStateRouteChange(transition.to, e.target.value)}
                                  disabled={!isEditable}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                >
                                  <option value="">未設定</option>
                                  {approvalRouteKeys.map((routeKey) => (
                                    <option key={routeKey} value={routeKey}>
                                      {routeKey === 'MAIN' ? '通常ルート' : routeKey}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <span style={{ color: '#9ca3af' }}>ー</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>

                  {workflowTransitions.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      ワークフローに遷移が定義されていません
                    </div>
                  )}

                  {isEditable && workflowTransitions.length > 0 && (
                    <div style={{ marginTop: '1.5rem' }}>
                      <Button onClick={handleSaveStateRoutes} variant="primary">
                        ステータス/承認ルート割当を保存
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'lint' && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">整合性チェック結果</h2>

              {!lintResult ? (
                <div className="text-center py-8 text-gray-500">
                  整合性チェックを実行してください
                </div>
              ) : (
                <div>
                  <div style={{
                    padding: '1rem',
                    borderRadius: '0.375rem',
                    backgroundColor: lintResult.pass ? '#dcfce7' : '#fee2e2',
                    color: lintResult.pass ? '#166534' : '#991b1b',
                    marginBottom: '1.5rem',
                    fontWeight: 600
                  }}>
                    {lintResult.pass ? '✓ 整合性チェックをパスしました' : '✗ 整合性チェックに失敗しました'}
                  </div>

                  {lintResult.errors.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#dc2626', marginBottom: '0.5rem' }}>
                        エラー ({lintResult.errors.length}件)
                      </h3>
                      <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                        {lintResult.errors.map((err, index) => (
                          <li key={index} style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            {err}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {lintResult.warnings.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#f59e0b', marginBottom: '0.5rem' }}>
                        警告 ({lintResult.warnings.length}件)
                      </h3>
                      <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem' }}>
                        {lintResult.warnings.map((warn, index) => (
                          <li key={index} style={{ color: '#f59e0b', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                            {warn}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {lintResult.pass && lintResult.warnings.length === 0 && lintResult.errors.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      問題は見つかりませんでした
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </>
      )}
    </div>
  )
}
